import { useMemo } from 'react';

import useLocaleStore from '@/stores/LocaleStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { CUSTOMER_DOMAIN } from '@/types/customerConst';
import { ACCOUNT_COLUMNS, CONTACT_COLUMNS } from '@/types/filterKeyConst';
import { createOptionsFromValues } from '@/utils/util';

const useCustomerSegmentFilter = (selectedDomain: CUSTOMER_DOMAIN) => {
  const workspaceFieldOptions = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );

  const regionMap = useLocaleStore((state) => state.regionMap);
  const accountAttrs: FilterOptionT[] = useMemo(() => {
    return [
      {
        columnKey: ACCOUNT_COLUMNS.INDUSTRY,
        columnName: 'Industry',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.INDUSTRY,
          workspaceFieldOptions.industry_values,
        ),
      },
      {
        columnKey: ACCOUNT_COLUMNS.COUNTRY,
        columnName: 'Country',
        searchable: true,
        filterList: Object.keys(regionMap)
          .sort((a, b) =>
            regionMap[a].country.localeCompare(regionMap[b].country),
          )
          .map((key) => {
            return {
              columnKey: ACCOUNT_COLUMNS.COUNTRY,
              filterKey: key,
              filterName: regionMap[key].country,
              filterOptionNode: regionMap[key].country,
            };
          }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.RATING,
        columnName: 'Rating',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.RATING,
          workspaceFieldOptions.rating_values,
        ),
      },
      {
        columnKey: ACCOUNT_COLUMNS.SIZE,
        columnName: 'Size',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.SIZE,
          workspaceFieldOptions.size_values,
        ),
      },
      {
        columnKey: ACCOUNT_COLUMNS.PLAN,
        columnName: 'Plan',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.PLAN,
          workspaceFieldOptions.plan_values,
        ),
      },
      {
        columnKey: ACCOUNT_COLUMNS.TYPE,
        columnName: 'Type',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.TYPE,
          workspaceFieldOptions.account_type_values,
        ),
      },
      {
        columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
        columnName: 'Primary Usecase',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.PRIMARY_USECASE,
          workspaceFieldOptions.account_primary_usecase_values,
        ),
      },
      {
        columnKey: ACCOUNT_COLUMNS.STAGE,
        columnName: 'Stage',
        searchable: true,
        filterList: createOptionsFromValues(
          ACCOUNT_COLUMNS.STAGE,
          workspaceFieldOptions.account_stage_values,
        ),
      },
    ];
  }, [
    regionMap,
    workspaceFieldOptions.account_primary_usecase_values,
    workspaceFieldOptions.account_stage_values,
    workspaceFieldOptions.account_type_values,
    workspaceFieldOptions.industry_values,
    workspaceFieldOptions.plan_values,
    workspaceFieldOptions.rating_values,
    workspaceFieldOptions.size_values,
  ]);

  const contactAttrs: FilterOptionT[] = useMemo(() => {
    return [
      {
        columnKey: CONTACT_COLUMNS.PLAN,
        columnName: 'Plan',
        searchable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.PLAN,
          workspaceFieldOptions.contact_plan_values,
        ),
      },
      {
        columnKey: CONTACT_COLUMNS.STAGE,
        columnName: 'Stage',
        searchable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.STAGE,
          workspaceFieldOptions.contact_stage_values,
        ),
      },
      {
        columnKey: CONTACT_COLUMNS.TYPE,
        columnName: 'Type',
        searchable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.TYPE,
          workspaceFieldOptions.contact_type_values,
        ),
      },
      {
        columnKey: CONTACT_COLUMNS.COUNTRY,
        columnName: 'Country',
        searchable: true,
        filterList: Object.keys(regionMap)
          .sort((a, b) =>
            regionMap[a].country.localeCompare(regionMap[b].country),
          )
          .map((key) => {
            return {
              columnKey: CONTACT_COLUMNS.COUNTRY,
              filterKey: key,
              filterName: regionMap[key].country,
              filterOptionNode: regionMap[key].country,
            };
          }),
      },
      {
        columnKey: CONTACT_COLUMNS.JOB_FUNCTION,
        columnName: 'Job function',
        searchable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.JOB_FUNCTION,
          workspaceFieldOptions.job_function_values,
        ),
      },
    ];
  }, [
    regionMap,
    workspaceFieldOptions.contact_plan_values,
    workspaceFieldOptions.contact_stage_values,
    workspaceFieldOptions.contact_type_values,
    workspaceFieldOptions.job_function_values,
  ]);

  const attrOptions = useMemo(() => {
    if (selectedDomain === CUSTOMER_DOMAIN.ACCOUNT) return accountAttrs;
    if (selectedDomain === CUSTOMER_DOMAIN.CONTACT) return contactAttrs;
    throw new Error('Invalid selected domain');
  }, [accountAttrs, contactAttrs, selectedDomain]);

  return {
    accountAttrs,
    contactAttrs,
    attrOptions,
  };
};

export default useCustomerSegmentFilter;
